import styled, { keyframes } from "styled-components";
import { typography } from "../styles";

/** Lovable dropdown-menu: fade-in + zoom-in-95 + slide-in-from-top-2 */
const notifPanelIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-0.5rem) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const NotifRoot = styled.div`
  position: relative;
  display: block;
  flex-shrink: 0;
`;

/**
 * Lovable trigger:
 * relative p-2 rounded-full text-muted-foreground
 * hover:text-foreground hover:bg-muted/40 transition-colors
 */
export const NotifTrigger = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px; /* p-2 */
  border: none;
  border-radius: 999px;
  background: transparent;
  color: hsl(var(--hp-muted-foreground));
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  svg {
    width: 20px; /* h-5 w-5 */
    height: 20px;
    flex-shrink: 0;
  }

  &:hover,
  &[aria-expanded="true"] {
    color: hsl(var(--hp-foreground));
    background: hsl(var(--hp-muted) / 0.4);
  }

  &:focus-visible {
    outline: 2px solid hsl(var(--hp-primary));
    outline-offset: 2px;
  }
`;

/**
 * Lovable badge:
 * absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
 * bg-primary text-primary-foreground text-[10px] font-semibold
 * flex items-center justify-center border border-background
 */
export const NotifBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border: 1px solid hsl(var(--hp-background));
  border-radius: 999px;
  background: hsl(var(--hp-primary));
  color: hsl(var(--hp-primary-foreground));
  font-family: ${typography.fontFamily};
  font-size: 10px;
  font-weight: ${typography.weights.semibold};
  line-height: 1;
  pointer-events: none;
`;

/**
 * Transparent dismiss layer — Radix DropdownMenu modal scroll lock.
 */
export const NotifScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1390;
  background: transparent;
  touch-action: none;
`;

/**
 * Lovable DropdownMenuContent base + NotificationsMenu overrides:
 * base: text-popover-foreground shadow-md + open animation
 * override: w-[22rem] p-0 rounded-2xl border-border/60 bg-card/95 backdrop-blur-xl
 */
export const NotifPanel = styled.div`
  position: fixed;
  z-index: 1400;
  display: flex;
  flex-direction: column;
  width: 22rem;
  max-width: calc(100vw - 24px);
  padding: 0;
  overflow: hidden;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 1rem; /* rounded-2xl */
  background: hsl(var(--hp-card) / 0.95);
  color: hsl(var(--hp-popover-foreground));
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  transform-origin: top right;
  animation: ${notifPanelIn} 150ms ease-out;
`;

/** Lovable: flex items-center justify-between px-4 py-3 border-b border-border/50 */
export const NotifPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid hsl(var(--hp-border) / 0.5);
`;

/** Lovable: font-semibold text-foreground (body 16px) */
export const NotifPanelTitle = styled.span`
  color: hsl(var(--hp-foreground));
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.5;
`;

/** Lovable: text-xs text-muted-foreground */
export const NotifPanelUnread = styled.span`
  color: hsl(var(--hp-muted-foreground));
  font-family: ${typography.fontFamily};
  font-size: 0.75rem;
  font-weight: ${typography.weights.normal};
  line-height: 1rem;
`;

/** Lovable: max-h-[22rem] overflow-y-auto (scroll wrapper around empty OR ul) */
export const NotifScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  max-height: 22rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`;

/** Lovable: ul divide-y divide-border/40 */
export const NotifList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  > li + li {
    border-top: 1px solid hsl(var(--hp-border) / 0.4);
  }
`;

/** Lovable: flex flex-col items-center gap-2 px-6 py-10 text-center */
export const NotifEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 24px;
  text-align: center;
`;

/** Lovable: h-11 w-11 rounded-full bg-muted/40 text-muted-foreground */
export const NotifEmptyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  background: hsl(var(--hp-muted) / 0.4);
  color: hsl(var(--hp-muted-foreground));

  svg {
    width: 20px;
    height: 20px;
  }
`;

/** Lovable: text-sm font-medium text-foreground */
export const NotifEmptyTitle = styled.div`
  color: hsl(var(--hp-foreground));
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.25rem;
`;

/** Lovable: text-xs text-muted-foreground */
export const NotifEmptyDesc = styled.p`
  margin: 0;
  color: hsl(var(--hp-muted-foreground));
  font-family: ${typography.fontFamily};
  font-size: 0.75rem;
  font-weight: ${typography.weights.normal};
  line-height: 1rem;
`;

/**
 * Lovable item button:
 * w-full flex gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40
 * unread: bg-primary/[0.06]
 */
export const NotifItemButton = styled.button`
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${({ $unread }) =>
    $unread ? "hsl(var(--hp-primary) / 0.06)" : "transparent"};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
  font-family: inherit;
  color: inherit;

  &:hover {
    background: hsl(var(--hp-muted) / 0.4);
  }

  &:focus-visible {
    outline: 2px solid hsl(var(--hp-primary));
    outline-offset: -2px;
  }
`;

/** Lovable Avatar: h-9 w-9 shrink-0 border border-border/60 */
export const NotifAvatar = styled.img`
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 50%;
  object-fit: cover;
`;

/** Lovable AvatarFallback: text-[11px] */
export const NotifAvatarFallback = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 50%;
  background: hsl(var(--hp-muted));
  color: hsl(var(--hp-foreground));
  font-family: ${typography.fontFamily};
  font-size: 11px;
  font-weight: ${typography.weights.semibold};
  line-height: 1;
`;

export const NotifItemBody = styled.div`
  min-width: 0;
  flex: 1;
`;

/** Lovable: flex items-center gap-2 */
export const NotifMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * Lovable type chip:
 * text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border
 * Alert: primary · Update: amber-500 · Post: sky-500 · Reply: emerald-500
 */
export const NotifTypeChip = styled.span`
  padding: 2px 6px;
  border: 1px solid;
  border-radius: 0.25rem;
  font-family: ${typography.fontFamily};
  font-size: 10px;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.025em; /* tracking-wide */
  line-height: 1.2;
  text-transform: uppercase;

  ${({ $type }) => {
    if ($type === "alert") {
      return `
        border-color: hsl(var(--hp-primary) / 0.3);
        background: hsl(var(--hp-primary) / 0.15);
        color: hsl(var(--hp-primary));
      `;
    }
    if ($type === "update") {
      /* Tailwind amber-500 #f59e0b */
      return `
        border-color: hsl(38 92% 50% / 0.3);
        background: hsl(38 92% 50% / 0.15);
        color: hsl(38 92% 50%);
      `;
    }
    if ($type === "post") {
      /* Tailwind sky-500 #0ea5e9 */
      return `
        border-color: hsl(199 89% 48% / 0.3);
        background: hsl(199 89% 48% / 0.15);
        color: hsl(199 89% 48%);
      `;
    }
    /* Tailwind emerald-500 #10b981 */
    return `
      border-color: hsl(160 84% 39% / 0.3);
      background: hsl(160 84% 39% / 0.15);
      color: hsl(160 84% 39%);
    `;
  }}
`;

/** Lovable: h-1.5 w-1.5 rounded-full bg-primary */
export const NotifUnreadDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 999px;
  background: hsl(var(--hp-primary));
  flex-shrink: 0;
`;

/** Lovable: mt-1 text-sm text-foreground leading-snug */
export const NotifItemText = styled.p`
  margin: 4px 0 0;
  color: hsl(var(--hp-foreground));
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  line-height: 1.375; /* leading-snug */

  .notif-author {
    font-weight: ${typography.weights.medium};
    color: hsl(var(--hp-foreground));
  }

  .notif-action {
    color: hsl(var(--hp-muted-foreground));
    font-weight: ${typography.weights.normal};
  }
`;

/** Lovable: mt-1 text-xs text-muted-foreground — date-fns "MMM d, yyyy · h:mm a" */
export const NotifItemDate = styled.p`
  margin: 4px 0 0;
  color: hsl(var(--hp-muted-foreground));
  font-family: ${typography.fontFamily};
  font-size: 0.75rem;
  font-weight: ${typography.weights.normal};
  line-height: 1rem;
`;

/** Lovable: border-t border-border/50 */
export const NotifFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid hsl(var(--hp-border) / 0.5);
`;

/**
 * Lovable footer button:
 * w-full flex items-center justify-center gap-2 px-4 py-3
 * text-sm font-medium text-primary hover:bg-muted/40 transition-colors
 */
export const NotifFooterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: hsl(var(--hp-primary));
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.25rem;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    width: 16px; /* h-4 w-4 */
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    background: hsl(var(--hp-muted) / 0.4);
  }

  &:focus-visible {
    outline: 2px solid hsl(var(--hp-primary));
    outline-offset: -2px;
  }
`;
